import {
  browserLocalPersistence,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  useDeviceLanguage,
  type Auth,
  type ConfirmationResult,
  type UserCredential,
} from 'firebase/auth'
import { POSITION } from 'vue-toastification'
import { useFirebaseAuth } from 'vuefire'
import { stopPriorityPatientWebWorker } from '~/utils/patients/initiatePriorityPatientWorker'
import { stopDeviceActivityWebWorker } from '~/utils/initateDeviceActivityWorker'
declare global {
  interface Window {
    recaptchaVerifier: any
    recaptchaWidgetId: any
    isReflectTest?: boolean
  }
}
export default function useAuth() {
  const config = useRuntimeConfig()
  const user = useUserStore()
  const navigationStore = useNavigationStore()
  const multiFactorOtp = ref<any>('')
  const auth = useFirebaseAuth() as Auth
  const { $toast: toast, $api: api } = useNuxtApp()

  let firebaseConfirmationResult: ConfirmationResult
  let firebaseConfirmation: UserCredential

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      if (auth.currentUser) {
        // First reauthenticate with the old password
        const credential = EmailAuthProvider.credential(auth.currentUser.email as string, oldPassword)
        await reauthenticateWithCredential(auth.currentUser, credential)

        // Then update to the new password
        await updatePassword(auth.currentUser, newPassword)

        toast.success('Le mot de passe a été changé avec succès', {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
        return true
      }
      return false
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        toast.error("L'ancien mot de passe est incorrect", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      } else if (error.code === 'auth/weak-password') {
        toast.error('Le nouveau mot de passe est trop faible', {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      } else if (error.code === 'auth/too-many-requests') {
        toast.warning("Trop d'essais, réessayez plus tard.", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      } else {
        toast.error(error.message, {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      }
      return false
    }
  }

  const sentResetPasswordLink = async (email: string) => {
    try {
      const result = await api.auth.resetPasswordRequest({ email })
      // await sendPasswordResetEmail(auth, email)
    } catch (error: any) {
      if (error && error.code === 'auth/user-not-found') {
        // Show an error below the phone number input
        toast.error('Utilisateur inconnu ou mauvais mot de passe', {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      } else if (error.code === 'auth/too-many-requests') {
        toast.warning("Trop d'essais, réessayez plus tard.", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      } else {
        toast.error("Une erreur s'est produite lors de la connexion", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      }
      return false
    }
  }

  const showCustomError = (code: string) => {
    const errorCode: any = {
      'auth/user-not-found': "Il n'y a pas d'utilisateur correspondant à cet identifiant",
      'auth/expired-action-code': 'Votre mot de passe a été expiré',
      'auth/too-many-requests': "Trop d'essais, réessayez plus tard.",
      default: 'Veuillez vérifier vos identifiants',
    }
    toast.warning(errorCode[code] || errorCode.default, {
      timeout: 5000,
      position: POSITION.TOP_LEFT,
    })
  }

  let logginErrorCount = 0
  const sendEmailAndPassword = async (email: string, password: string, recaptchaVerifier: RecaptchaVerifier) => {
    try {
      firebaseConfirmation = await setPersistence(auth, browserLocalPersistence).then(() => {
        return signInWithEmailAndPassword(auth, email, password)
      })
      const token = await firebaseConfirmation.user.getIdToken()
      localStorage.removeItem('closedAt')
      localStorage.removeItem('loggedOutAt')
      user.save(firebaseConfirmation.user, token)

      api.setToken(token)
      return {
        status: 'SIGNED',
        isSuccess: true,
      }
    } catch (error: any) {
      if (error && error.code === 'auth/multi-factor-auth-required') {
        const resolver: any = getMultiFactorResolver(auth, error)
        // Map enrolled second factors to display names
        const enrolledFactors = resolver.hints.map((hint: any) => hint.displayName || 'Unknown')
        const phoneFactor = resolver.hints.find((hint: any) => hint.factorId === 'phone')

        if (phoneFactor) {
          try {
            const phoneInfoOptions = {
              // phoneNumber: '+33612465993', // Phone number from hints
              multiFactorHint: resolver.hints[0],
              session: resolver.session,
            }

            const phoneAuthProvider = new PhoneAuthProvider(auth)
            console.log('phoneAuthProvider', phoneAuthProvider)
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
            console.log('verificationId', verificationId)
            // Prompt the user for the verification code
            multiFactorOtp.value = {
              verificationId,
              resolver,
            }
            return {
              status: 'OTP_SENT',
              hint: phoneInfoOptions.multiFactorHint.phoneNumber,
              isSuccess: true,
            }
          } catch (phoneError) {
            console.error('Phone verification error:', phoneError)
            return {
              status: 'OTP_ERROR',
              isSuccess: false,
            }
          }
        }
        // toast.warning(enrolledFactors.join(','), {
        //   timeout: 5000,
        // position: POSITION.TOP_LEFT,
        // })
        console.log('enrolledFactors')
        // console.log(enrolledFactors)
      } else {
        logginErrorCount++
        console.log(error)
        // if (logginErrorCount > 4) {
        //   showCustomError('auth/too-many-requests')
        // } else {
        //   showCustomError(error?.code)
        // }

        return {
          status: 'ERROR',
          isSuccess: false,
        }
      }
    }
  }

  const multiFactorWithSMS = async (otp: string) => {
    try {
      const credential = PhoneAuthProvider.credential(multiFactorOtp.value.verificationId, otp)
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential)
      // Resolve the MFA challenge with the OTP credential
      const userCredential = await multiFactorOtp.value.resolver.resolveSignIn(multiFactorAssertion)
      localStorage.removeItem('closedAt')
      localStorage.removeItem('loggedOutAt')
      const token = userCredential._tokenResponse?.idToken
      user.save(userCredential.user, token, 'email')
      api.setToken(token)
      multiFactorOtp.value = ''
    } catch (error: any) {
      // if (error && error.code === 'auth/invalid-verification-code') {
      //   // Show an error below the phone number input
      //   toast.error("Le code de vérification n'est pas le bon.", {
      //     timeout: 5000,
      //     position: POSITION.TOP_LEFT,
      //   })
      //   return error
      // } else {
      //   toast.error("Une erreur s'est produite.", {
      //     timeout: 5000,
      //     position: POSITION.TOP_LEFT,
      //   })
      // }
      return false
    }
  }

  const sendPhoneNumber = async (recaptchaVerifier: RecaptchaVerifier, phone: string) => {
    try {
      let formatedPhone = phone.split(' ').join('')
      if (formatedPhone.charAt(0) !== '+' && formatedPhone.charAt(0) === '0')
        formatedPhone = formatedPhone.replace('0', '+33')
      firebaseConfirmationResult = await setPersistence(auth, browserLocalPersistence).then(() => {
        return signInWithPhoneNumber(auth, formatedPhone, recaptchaVerifier)
      })

      return true
    } catch (error: any) {
      if (error && (error.code === 'auth/invalid-phone-number' || error.code === 'auth/admin-restricted-operation')) {
        // Show an error below the phone number input
        toast.error("Le numéro de téléphone mobile n'est pas le bon.", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })

        // Reset ReCaptcha to let user retry after an error.
        // https://firebase.google.com/docs/auth/web/phone-auth#send-a-verification-code-to-the-users-phone
        // NB: `_delegate` is is mandatory here as the v8compat/v9 integration of firebase isn't well supported
        window.recaptchaVerifier.recaptcha.reset(window.recaptchaWidgetId)
        return error
      } else if (error.code === 'auth/too-many-requests') {
        toast.warning("Trop d'essais, réessayez plus tard.", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      } else {
        toast.error("Une erreur s'est produite.", {
          timeout: 5000,
          position: POSITION.TOP_LEFT,
        })
      }
      return false
    }
  }

  const sendSMSCode = async (otp: string) => {
    try {
      // Send the otp that the user got from SMS to Firebase
      const firebaseResponse = await firebaseConfirmationResult.confirm(otp)
      // Send the user information storage
      const token = await firebaseResponse.user.getIdToken()
      localStorage.removeItem('closedAt')
      localStorage.removeItem('loggedOutAt')
      user.save(firebaseResponse.user, token)
      api.setToken(token)
      //this.$axios.setToken(token, 'Bearer')
      /*this.$store.dispatch('user/save', {
        user: firebaseResponse.user,
        firebaseAccessToken: token,
      })*/
      return true
    } catch (error: any) {
      if (error && error.code === 'auth/invalid-verification-otp') {
        //$toast.error("Le code OTP n'est pas le bon", { timeout: 5000 })
      } else {
        toast.error("Le code OTP n'est pas le bon", { timeout: 5000 })
      }
      return false
    }
  }
  const signOut = async () => {
    localStorage.removeItem('closedAt')
    localStorage.removeItem('loggedOutAt')
    try {
      if (user.user.isPatient) navigationStore.setWasPatient()
      if (auth) await auth.signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
    stopPriorityPatientWebWorker()
    stopDeviceActivityWebWorker()
    window.location.reload()
    const keyToKeep = 'rememberUser'
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (typeof key === 'string' && key !== keyToKeep) {
        localStorage.removeItem(key)
      }
    }
  }
  const applyRecaptcha = async () => {
    try {
      // Set the found device language as default for auth service
      useDeviceLanguage(auth)
      if (window.isReflectTest) {
        auth.settings.appVerificationDisabledForTesting = true
      }
      // auth.settings.appVerificationDisabledForTesting = ['staging', 'develop'].includes(config.public.ENVIRONMENT)
      // Setup an invisible Captcha to ensure the user is not a robot
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha')
      // Prerender the ReCaptcha to make it appear on page load
      // Also store the ReCaptcha widget ID in order to reset the widget upon retry
      window.recaptchaWidgetId = await window.recaptchaVerifier.render()
    } catch (error: any) {
      const route = useRoute()
      if (route.path !== '/auth/psc/') {
        toast.error(
          'Firebase authentication failed on Recaptcha initialization.' +
            error.code +
            '\n\n' +
            'Raison(s) :\n' +
            error.message,
          { timeout: 5000 },
        )
      }
    }
  }

  return {
    sendEmailAndPassword,
    changePassword,
    sentResetPasswordLink,
    sendPhoneNumber,
    sendSMSCode,
    applyRecaptcha,
    signOut,
    auth,
    multiFactorWithSMS,
  }
}
